import { emitAuthDiagnostic } from './authDiagnostics';
import {
  traceWebAuthBootstrapRequestCompleted,
  traceWebAuthBootstrapRequestFailed,
  traceWebAuthBootstrapRequestRecovered,
  traceWebAuthBootstrapRequestStarted,
  traceWebAuthLoadClerkSessionCompleted,
  traceWebAuthTokenResolutionCompleted,
} from './bootstrapTraceEvents';
import { describeJwtToken } from './tokenMetadata';
import {
  isMissingUserIdentityError,
  isSessionTokenExpiredError,
  isUserNotFoundError,
  resolveTokenFromClerkClientSessions,
  wait,
} from './useAuth.helpers';
import type { AuthSession } from './types';

type LoadClerkSessionArgs = {
  client: unknown;
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
  currentUserId: string | null;
  allowProvisioning: boolean;
  forceTokenRefresh?: boolean;
  bootstrapSession: (
    accessToken: string,
    opts?: { allowProvisioning?: boolean },
  ) => Promise<AuthSession>;
  clearProvisioningFlag: () => void;
};

export const loadClerkSession = async ({
  client,
  getToken,
  currentUserId,
  allowProvisioning,
  forceTokenRefresh = false,
  bootstrapSession,
  clearProvisioningFlag,
}: LoadClerkSessionArgs): Promise<AuthSession | null> => {
  const loadStartedAt = Date.now();
  let previousToken: string | null = null;
  const resolveClerkToken = async (
    skipCache: boolean,
  ): Promise<{ token: string | null; attemptCount: number }> => {
    let token: string | null = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      token = await getToken(skipCache ? { skipCache: true } : undefined);
      token ??= await resolveTokenFromClerkClientSessions(
        client,
        skipCache ? { skipCache: true } : undefined,
      );
      if (token) return { token, attemptCount: attempt + 1 };
      await wait(100 * (attempt + 1));
    }
    return { token: null, attemptCount: 3 };
  };

  const initialTokenResolution = await resolveClerkToken(forceTokenRefresh);
  let token = initialTokenResolution.token;
  const initialTokenMetadata = describeJwtToken(token);
  traceWebAuthTokenResolutionCompleted({
    userId: currentUserId,
    durationMs: Date.now() - loadStartedAt,
    attemptCount: initialTokenResolution.attemptCount,
    skipCache: forceTokenRefresh,
    tokenResolved: initialTokenMetadata.tokenResolved,
    tokenChanged: null,
    tokenLength: initialTokenMetadata.tokenLength,
    signatureSuffix: initialTokenMetadata.signatureSuffix,
    subject: initialTokenMetadata.subject,
    issuedAtMs: initialTokenMetadata.issuedAtMs,
    expiresAtMs: initialTokenMetadata.expiresAtMs,
    issuedAgoMs: initialTokenMetadata.issuedAgoMs,
    expiresInMs: initialTokenMetadata.expiresInMs,
  });
  emitAuthDiagnostic('clerk_token_resolution_completed', {
    userId: currentUserId,
    skipCache: forceTokenRefresh,
    attemptCount: initialTokenResolution.attemptCount,
    tokenChanged: null,
    ...initialTokenMetadata,
  });
  previousToken = token;
  if (!token) {
    traceWebAuthLoadClerkSessionCompleted({
      userId: currentUserId,
      durationMs: Date.now() - loadStartedAt,
      outcome: 'no_token',
    });
    return null;
  }

  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (token === null) {
      break;
    }
    const activeToken = token;
    const bootstrapStartedAt = Date.now();
    traceWebAuthBootstrapRequestStarted({
      userId: currentUserId,
      attempt: attempt + 1,
      allowProvisioning,
    });
    try {
      const nextSession = await bootstrapSession(activeToken, { allowProvisioning });
      traceWebAuthBootstrapRequestCompleted({
        userId: nextSession.userId,
        attempt: attempt + 1,
        allowProvisioning,
        durationMs: Date.now() - bootstrapStartedAt,
      });
      traceWebAuthLoadClerkSessionCompleted({
        userId: nextSession.userId,
        durationMs: Date.now() - loadStartedAt,
        outcome: 'success',
      });
      if (allowProvisioning) {
        clearProvisioningFlag();
      }
      return nextSession;
    } catch (caughtError) {
      if (isMissingUserIdentityError(caughtError)) {
        if (allowProvisioning) return null;
        const recovered = await bootstrapSession(activeToken, {
          allowProvisioning: true,
        });
        traceWebAuthBootstrapRequestRecovered({
          userId: recovered.userId,
          attempt: attempt + 1,
          recoveryReason: 'missing_user_identity',
          durationMs: Date.now() - bootstrapStartedAt,
        });
        traceWebAuthLoadClerkSessionCompleted({
          userId: recovered.userId,
          durationMs: Date.now() - loadStartedAt,
          outcome: 'recovered',
        });
        clearProvisioningFlag();
        return recovered;
      }
      if (isUserNotFoundError(caughtError)) {
        if (allowProvisioning) return null;
        const recovered = await bootstrapSession(activeToken, {
          allowProvisioning: true,
        });
        traceWebAuthBootstrapRequestRecovered({
          userId: recovered.userId,
          attempt: attempt + 1,
          recoveryReason: 'user_not_found',
          durationMs: Date.now() - bootstrapStartedAt,
        });
        traceWebAuthLoadClerkSessionCompleted({
          userId: recovered.userId,
          durationMs: Date.now() - loadStartedAt,
          outcome: 'recovered',
        });
        clearProvisioningFlag();
        return recovered;
      }
      traceWebAuthBootstrapRequestFailed({
        userId: currentUserId,
        attempt: attempt + 1,
        allowProvisioning,
        durationMs: Date.now() - bootstrapStartedAt,
        error: caughtError,
      });
      emitAuthDiagnostic('clerk_bootstrap_request_failed', {
        userId: currentUserId,
        attempt: attempt + 1,
        allowProvisioning,
        error: caughtError instanceof Error ? caughtError.message : String(caughtError),
        token: describeJwtToken(activeToken),
      });
      lastError = caughtError;
      if (isSessionTokenExpiredError(caughtError) && attempt < 1) {
        const refreshedTokenResolution = await resolveClerkToken(true);
        token = refreshedTokenResolution.token;
        const refreshedTokenMetadata = describeJwtToken(token);
        const hadPreviousResolvedToken = previousToken !== null;
        traceWebAuthTokenResolutionCompleted({
          userId: currentUserId,
          durationMs: Date.now() - loadStartedAt,
          attemptCount: refreshedTokenResolution.attemptCount,
          skipCache: true,
          tokenResolved: refreshedTokenMetadata.tokenResolved,
          tokenChanged: hadPreviousResolvedToken ? previousToken !== token : null,
          tokenLength: refreshedTokenMetadata.tokenLength,
          signatureSuffix: refreshedTokenMetadata.signatureSuffix,
          subject: refreshedTokenMetadata.subject,
          issuedAtMs: refreshedTokenMetadata.issuedAtMs,
          expiresAtMs: refreshedTokenMetadata.expiresAtMs,
          issuedAgoMs: refreshedTokenMetadata.issuedAgoMs,
          expiresInMs: refreshedTokenMetadata.expiresInMs,
        });
        emitAuthDiagnostic('clerk_token_resolution_completed', {
          userId: currentUserId,
          skipCache: true,
          attemptCount: refreshedTokenResolution.attemptCount,
          tokenChanged: hadPreviousResolvedToken ? previousToken !== token : null,
          ...refreshedTokenMetadata,
        });
        previousToken = token;
        if (token) {
          continue;
        }
      }
      if (attempt < 1) {
        await wait(150 * (attempt + 1));
      }
    }
  }
  traceWebAuthLoadClerkSessionCompleted({
    userId: currentUserId,
    durationMs: Date.now() - loadStartedAt,
    outcome: 'failed',
  });
  throw lastError;
};
